import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { RootState } from '../../app.model';
import { booleanLookup } from '../../../common/constants/common';
import { IConfigSoftwareFamilyState } from './configSoftwareFamily.model';
import {
  deleteConfigSoftwareFamily,
  getConfigSoftwareFamilyById,
  saveConfigSoftwareFamily,
  searchConfigSoftwareFamily,
} from './configSoftwareFamily.action';
import { IConfigSoftwareFamily } from '../../../services/master/configSoftwareFamily/configSoftwareFamily.model';

export const initialState: IConfigSoftwareFamilyState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const configSoftwareFamilySlice = createSlice({
  name: 'configSoftwareFamily',
  initialState,
  reducers: {
    clearConfigSoftwareFamily: () => {
      return initialState;
    },
    clearConfigSoftwareFamilyMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearConfigSoftwareFamilyGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchConfigSoftwareFamily.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchConfigSoftwareFamily.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { records, total_count, table_name, column_selection } = action.payload;
      state.search.data = records;
      state.search.count = total_count;
      state.search.lookups = { booleanLookup };
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = table_name;
      if (column_selection) {
        state.tableColumnSelection.id = column_selection.id;
        const tableSelectionObj = JSON.parse(column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = table_name;
    },
    [searchConfigSoftwareFamily.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getConfigSoftwareFamilyById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getConfigSoftwareFamilyById.fulfilled.type]: (
      state,
      action: PayloadAction<IConfigSoftwareFamily>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getConfigSoftwareFamilyById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveConfigSoftwareFamily.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveConfigSoftwareFamily.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveConfigSoftwareFamily.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteConfigSoftwareFamily.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteConfigSoftwareFamily.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteConfigSoftwareFamily.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const configSoftwareFamilySelector = (state: RootState) => state.configSoftwareFamily;

// Actions
export const {
  clearConfigSoftwareFamily,
  clearConfigSoftwareFamilyMessages,
  clearConfigSoftwareFamilyGetById,
  setTableColumnSelection,
} = configSoftwareFamilySlice.actions;

// The reducer
export default configSoftwareFamilySlice.reducer;
